import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, AlertCircle, Wifi, WifiOff, Users } from 'lucide-react';
import AgentCard from './AgentCard';

const ORCHESTRATOR_URL = 'http://localhost:8000';
const REFRESH_INTERVAL = 10000; // 10 seconds

// Static fallback org structure (used when API is offline / for demo)
const FALLBACK_ORG = {
  id: 'cao-orchestrator',
  name: 'Nellie Ross',
  role: 'CAO',
  model: 'claude-sonnet-4-5',
  port: 8000,
  status: 'unknown',
  uptime: null,
  tasks_processed: null,
  last_task: null,
  children: [
    {
      id: 'cao-cto',
      name: 'CTO Agent',
      role: 'CTO',
      model: 'xai/grok-4-1-fast-reasoning',
      port: 8001,
      status: 'unknown',
      uptime: null,
      tasks_processed: null,
      last_task: null,
      children: [],
    },
    {
      id: 'cao-cmo',
      name: 'CMO Agent',
      role: 'CMO',
      model: 'openai-codex/gpt-5.3-codex',
      port: 8002,
      status: 'unknown',
      uptime: null,
      tasks_processed: null,
      last_task: null,
      children: [],
    },
    {
      id: 'cao-cfo',
      name: 'CFO Agent',
      role: 'CFO',
      model: 'xai/grok-4-1-fast-reasoning',
      port: 8003,
      status: 'unknown',
      uptime: null,
      tasks_processed: null,
      last_task: null,
      children: [],
    },
    {
      id: 'cao-coo',
      name: 'COO Agent',
      role: 'COO',
      model: 'groq/llama-3.3-70b-versatile',
      port: 8004,
      status: 'unknown',
      uptime: null,
      tasks_processed: null,
      last_task: null,
      children: [
        {
          id: 'cao-worker-devops',
          name: 'DevOps Worker',
          role: 'WORKER_DEVOPS',
          model: 'groq/llama-3.3-70b-versatile',
          port: 8011,
          status: 'unknown',
          uptime: null,
          tasks_processed: null,
          last_task: null,
          children: [],
        },
        {
          id: 'cao-worker-email-ops',
          name: 'Email Ops Worker',
          role: 'WORKER_EMAIL_OPS',
          model: 'openai-codex/gpt-5.3-codex',
          port: 8012,
          status: 'unknown',
          uptime: null,
          tasks_processed: null,
          last_task: null,
          children: [],
        },
        {
          id: 'cao-worker-seo',
          name: 'SEO Worker',
          role: 'WORKER_SEO',
          model: 'openai-codex/gpt-5.3-codex',
          port: 8013,
          status: 'unknown',
          uptime: null,
          tasks_processed: null,
          last_task: null,
          children: [],
        },
      ],
    },
  ],
};

// Merge live health data into the static org tree
function mergeOrgData(node, healthMap) {
  const health = healthMap[node.id] || healthMap[node.role] || {};
  const merged = { ...node, ...health };
  if (node.children) {
    merged.children = node.children.map((child) => mergeOrgData(child, healthMap));
  }
  return merged;
}

// Flatten tree to array for task panel agent list
function flattenOrg(node, result = []) {
  result.push(node);
  (node.children || []).forEach((child) => flattenOrg(child, result));
  return result;
}

// Connector line SVG between parent and children
function ConnectorLine({ count }) {
  if (count === 0) return null;
  return (
    <div className="flex justify-center">
      <div className="w-px h-6 bg-gradient-to-b from-[#7c3aed]/60 to-[#7c3aed]/20" />
    </div>
  );
}

// Horizontal connector for siblings
function SiblingConnector({ count }) {
  if (count <= 1) return null;
  return (
    <div className="flex justify-center mb-0">
      <div
        className="h-px bg-gradient-to-r from-transparent via-[#7c3aed]/40 to-transparent"
        style={{ width: `calc(${((count - 1) / count) * 100}% + 1px)` }}
      />
    </div>
  );
}

// Recursive org chart node renderer
function OrgNode({ node, onAgentClick, selectedAgent, depth = 0 }) {
  const hasChildren = node.children && node.children.length > 0;
  const isRoot = depth === 0;
  const isWorker = node.role && node.role.startsWith('WORKER_');

  return (
    <div className="flex flex-col items-center">
      {/* Agent card */}
      <div className={`${isRoot ? 'w-56' : isWorker ? 'w-44' : 'w-48'}`}>
        <AgentCard
          agent={node}
          onClick={onAgentClick}
          isSelected={selectedAgent?.id === node.id}
          compact={isWorker}
        />
      </div>

      {/* Children */}
      {hasChildren && (
        <>
          <ConnectorLine count={node.children.length} />
          {/* Horizontal bar across children */}
          {node.children.length > 1 && (
            <div className="relative flex justify-center w-full mb-0">
              <div
                className="h-px bg-gradient-to-r from-transparent via-[#7c3aed]/30 to-transparent"
                style={{
                  width: `${Math.min(95, (node.children.length - 1) * 18 + 20)}%`,
                }}
              />
            </div>
          )}
          {/* Children row */}
          <div
            className={`flex gap-4 mt-0 items-start justify-center flex-wrap`}
          >
            {node.children.map((child) => (
              <div key={child.id} className="flex flex-col items-center">
                {node.children.length > 1 && (
                  <div className="w-px h-4 bg-[#7c3aed]/30" />
                )}
                <OrgNode
                  node={child}
                  onAgentClick={onAgentClick}
                  selectedAgent={selectedAgent}
                  depth={depth + 1}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function OrgChart({ onAgentSelect, selectedAgent }) {
  const [orgData, setOrgData] = useState(FALLBACK_ORG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchOrgData = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${ORCHESTRATOR_URL}/org`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      clearTimeout(timeout);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // API may return flat agents list or tree structure
      if (data.agents && Array.isArray(data.agents)) {
        // Build health map from flat list
        const healthMap = {};
        data.agents.forEach((agent) => {
          const key = agent.id || agent.container_name || agent.name;
          if (key) healthMap[key] = agent;
          if (agent.role) healthMap[agent.role] = agent;
        });
        setOrgData(mergeOrgData(FALLBACK_ORG, healthMap));
      } else if (data.name && data.role) {
        // API returned tree directly
        setOrgData(data);
      } else if (data.health) {
        // Health map format
        setOrgData(mergeOrgData(FALLBACK_ORG, data.health));
      } else {
        // Unknown format — try treating as health map
        setOrgData(mergeOrgData(FALLBACK_ORG, data));
      }

      setConnected(true);
      setError(null);
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Orchestrator timed out (5s). Showing cached structure.');
      } else {
        setError(`Cannot reach orchestrator at ${ORCHESTRATOR_URL}/org — showing static layout.`);
      }
      setConnected(false);
      // Keep showing whatever org we have (fallback or last good fetch)
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchOrgData();
  }, [fetchOrgData]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    intervalRef.current = setInterval(fetchOrgData, REFRESH_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchOrgData]);

  const agents = flattenOrg(orgData);
  const healthyCount = agents.filter((a) => a.status === 'healthy' || a.status === 'online').length;
  const totalCount = agents.length;

  const handleManualRefresh = () => {
    setLoading(true);
    fetchOrgData();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/40 bg-[#0a0e1a] flex-shrink-0">
        <div className="flex items-center gap-3">
          <Users className="w-4 h-4 text-[#7c3aed]" />
          <h2 className="text-sm font-semibold text-white">Live Org Chart</h2>

          {/* Connection status */}
          <span
            className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border ${
              connected
                ? 'text-green-400 bg-green-900/20 border-green-500/30'
                : 'text-red-400 bg-red-900/20 border-red-500/30'
            }`}
          >
            {connected ? (
              <Wifi className="w-3 h-3" />
            ) : (
              <WifiOff className="w-3 h-3" />
            )}
            {connected ? 'Live' : 'Offline'}
          </span>

          {/* Agent health count */}
          <span className="text-xs text-slate-500">
            {healthyCount}/{totalCount} healthy
          </span>
        </div>

        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-slate-600">
              {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all border border-slate-700/40 disabled:opacity-50"
            aria-label="Refresh org chart"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-2 px-4 py-2 bg-yellow-900/10 border-b border-yellow-500/20 text-yellow-400 text-xs">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Org chart body */}
      <div className="flex-1 overflow-auto p-6 flex items-start justify-center">
        {loading && !orgData ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <RefreshCw className="w-8 h-8 text-[#7c3aed] animate-spin" />
            <p className="text-sm text-slate-400">Loading org chart...</p>
          </div>
        ) : (
          <div className="min-w-max">
            {/* Layer labels */}
            <div className="flex flex-col gap-0">
              {/* CAO tier label */}
              <div className="text-center mb-2">
                <span className="inline-block px-3 py-0.5 rounded-full text-xs font-medium bg-[#7c3aed]/10 text-[#7c3aed] border border-[#7c3aed]/20">
                  Executive Tier — CAO
                </span>
              </div>

              <OrgNode
                node={orgData}
                onAgentClick={onAgentSelect}
                selectedAgent={selectedAgent}
                depth={0}
              />
            </div>

            {/* Legend */}
            <div className="mt-8 pt-4 border-t border-slate-700/30">
              <p className="text-xs text-slate-600 text-center mb-2">Model Color Legend</p>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  { label: 'Grok (xAI)', color: 'bg-purple-400' },
                  { label: 'Codex (OpenAI)', color: 'bg-green-400' },
                  { label: 'Groq / Llama', color: 'bg-blue-400' },
                  { label: 'Kimi', color: 'bg-orange-400' },
                  { label: 'Claude (Anthropic)', color: 'bg-violet-400' },
                ].map(({ label, color }) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${color}`} />
                    <span className="text-xs text-slate-500">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer auto-refresh notice */}
      <div className="px-4 py-2 border-t border-slate-700/30 flex items-center justify-center">
        <p className="text-xs text-slate-700">
          Auto-refreshes every 10s &middot; Click any agent card to dispatch a task
        </p>
      </div>
    </div>
  );
}

// Export flat agent list helper for parent components
export { flattenOrg, FALLBACK_ORG };
